import { useMemo } from "react";
import type { ClientSideTableFilterFunctions } from "~components/DataTable/types/clientSideTableFilterFunctions.ts";
import type { ClientSideTableSearchField } from "~components/DataTable/types/clientSideTableSearchField.ts";

type Props<T> = {
  data: T[];
  currentFilter: Record<string, unknown>;
  filterFunctions?: ClientSideTableFilterFunctions<T>;
  searchText?: string;
  searchFields?: Array<ClientSideTableSearchField<T>>;
};

type Result<T> = {
  filteredData: T[];
};

const getSelectedValue = (value: unknown): unknown => {
  if (typeof value === "object" && value !== null && "value" in value) {
    return value.value;
  }

  return value;
};

const getSelectedFilterValues = (filterValue: unknown): unknown[] => {
  if (filterValue === undefined || filterValue === null) {
    return [];
  }

  if (Array.isArray(filterValue)) {
    return filterValue.map(getSelectedValue);
  }

  return [getSelectedValue(filterValue)];
};

export const useClientSideTableFilter = <T extends object>({
  data,
  currentFilter,
  filterFunctions,
  searchText,
  searchFields = [],
}: Props<T>): Result<T> => {
  const filteredData = useMemo(() => {
    let filtered = data;

    Object.entries(currentFilter).forEach(([filterKey, filterValue]) => {
      const selectedValues = getSelectedFilterValues(filterValue);

      if (selectedValues.length === 0) {
        return;
      }

      const filterFunction = filterFunctions?.[filterKey];
      if (filterFunction) {
        filtered = filtered.filter((record) => filterFunction(record, selectedValues));
      }
    });

    if (!searchText || searchFields.length === 0) {
      return filtered;
    }

    const lowerSearchText = searchText.toLowerCase();

    return filtered.filter((record) =>
      searchFields.some((searchField) => {
        const fieldValue = record[searchField];

        return fieldValue !== undefined && fieldValue !== null && String(fieldValue).toLowerCase().includes(lowerSearchText);
      }),
    );
  }, [data, currentFilter, filterFunctions, searchText, searchFields]);

  return { filteredData };
};
